"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Upload, Globe, Zap, Code, Shield, Download, ArrowRight, CheckCircle, Key, Users, Workflow, FileText, BookOpen, Link as LinkIcon, Infinity, DollarSign } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { MiniCalculator } from "@/components/mini-calculator"

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <Infinity className="w-3 h-3 mr-1" />
            Localization Without Limits, AI Without Markup
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Unlimited Features, Zero Restrictions
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need for enterprise-scale localization without enterprise pricing. No per-word fees, no user limits, no language caps.
          </p>
        </div>

        {/* Core Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardHeader>
              <Upload className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle>∞ Unlimited File Uploads</CardTitle>
              <CardDescription>
                Upload unlimited files of any size - no restrictions, no overage fees
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Support for nested JSON structures</li>
                <li>• Unlimited batch file uploads</li>
                <li>• No file size limits</li>
                <li>• Zero upload fees</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
            <CardHeader>
              <Globe className="h-8 w-8 text-green-600 mb-2" />
              <CardTitle>∞ All Languages Included</CardTitle>
              <CardDescription>
                Every language supported at no extra cost - scale globally without paying per language
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• 100+ major world languages</li>
                <li>• Regional dialects and variants</li>
                <li>• Cultural context awareness</li>
                <li>• No per-language pricing</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
            <CardHeader>
              <Code className="h-8 w-8 text-purple-600 mb-2" />
              <CardTitle>Complete Developer Tools</CardTitle>
              <CardDescription>
                REST API, CLI, and webhooks included - no additional API fees
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Full REST API with OpenAPI docs</li>
                <li>• Command-line interface</li>
                <li>• Unlimited webhook notifications</li>
                <li>• SDKs for popular languages</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-red-500/20 hover:border-red-400 dark:hover:border-red-600">
            <CardHeader>
              <Shield className="h-8 w-8 text-red-600 mb-2" />
              <CardTitle>Enterprise Security</CardTitle>
              <CardDescription>
                Bank-grade security for all users - no enterprise tier required
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• AES-256 encryption at rest</li>
                <li>• TLS 1.3+ for data in transit</li>
                <li>• Regular security audits</li>
                <li>• SOC 2 Type II compliance</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
            <CardHeader>
              <Download className="h-8 w-8 text-orange-600 mb-2" />
              <CardTitle>∞ Unlimited Downloads</CardTitle>
              <CardDescription>
                Download your files unlimited times in any format
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Multiple download formats</li>
                <li>• ZIP archives for batch files</li>
                <li>• Direct API access</li>
                <li>• No download limits or fees</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-yellow-500/20 hover:border-yellow-400 dark:hover:border-yellow-600">
            <CardHeader>
              <Zap className="h-8 w-8 text-yellow-600 mb-2" />
              <CardTitle>Lightning Fast Processing</CardTitle>
              <CardDescription>
                Powered by your own OpenAI key - no throttling or rate limits
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Sub-second response times</li>
                <li>• Parallel processing</li>
                <li>• Your own OpenAI rate limits</li>
                <li>• Real-time progress tracking</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Cost Comparison */}
        <section className="py-12 bg-muted/50">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-3">Enterprise Features, Startup Pricing</h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-lg">
                <span className="text-muted-foreground">Enterprise: $200-500/month</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-primary">Locsy: $22-30/month</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">All features included</span>
              </div>
            </div>
          </div>
        </section>

        {/* Mini Calculator */}
        <MiniCalculator />

        {/* Advanced Features */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Unlimited Advanced Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-blue-600" />
                  ∞ Bring Your Own Keys
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Use unlimited OpenAI API keys across projects for complete cost control and transparency.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Zero markup on API costs
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Multiple keys per project
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    AES-256 encrypted storage
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  ∞ Unlimited Team Members
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Invite unlimited translators, reviewers, and stakeholders at no extra cost.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    No per-user fees
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Role-based permissions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Real-time collaboration
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-purple-600" />
                  ∞ Magic Link Reviews
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Share unlimited secure review links with stakeholders for seamless collaboration.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    No account required for reviewers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Secure time-limited access
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Unlimited sharing
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  ∞ Project Cost Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Track AI costs per project with different API keys for complete transparency.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Per-project cost segregation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Real-time usage tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Model flexibility per project
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-red-500/20 hover:border-red-400 dark:hover:border-red-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-red-600" />
                  ∞ Unlimited Glossaries
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Create unlimited glossaries and style guides for consistent brand terminology.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Custom terminology databases
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Automatic style enforcement
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Brand consistency across languages
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-indigo-500/20 hover:border-indigo-400 dark:hover:border-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  Smart Context AI
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  AI understands context and provides culturally appropriate translations using your own models.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    UI context awareness
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Brand tone consistency
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Technical term handling
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Integration Examples */}
        <div className="bg-muted/30 rounded-3xl p-12 mb-20">
          <h2 className="text-3xl font-bold text-center mb-8">Easy Integration, Unlimited Usage</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">API Example</h3>
              <div className="bg-background rounded-lg p-4 border hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
                <pre className="text-sm text-muted-foreground overflow-x-auto">
{`curl -X POST 'https://api.locsy.ai/translate' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "file_id": "abc123",
    "target_languages": ["es", "fr", "de"]
  }'`}
                </pre>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ∞ Unlimited API calls included
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">CLI Example</h3>
              <div className="bg-background rounded-lg p-4 border hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
                <pre className="text-sm text-muted-foreground overflow-x-auto">
{`# Install CLI
npm install -g @locsy/cli

# Translate files
locsy translate en.json --to es,fr,de

# Auto-upload to project
locsy sync --project my-app`}
                </pre>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ∞ Unlimited CLI usage included
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to break free from per-word pricing?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of developers who've ditched expensive enterprise platforms for unlimited localization at a fraction of the cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/auth/signup" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 text-base h-12 px-8">
              Start Free Trial - No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link href="/pricing" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 text-base h-12 px-8">
              See How Much You Save
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Enterprise features without enterprise pricing • Everything unlimited
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
} 